import { useQuery } from '@tanstack/react-query';

import { type UsersResponse } from '../components/UserManagement/types';
import ExternalApiService, {
  type RequestableUsersProps,
} from '../services/ExternalService/ExternalApiService';

/**
 * Fetches requestable user data from the external API service.
 *
 * @async
 * @function fetchRequestableUsers
 * @param {RequestableUsersProps} userProps - The user properties required to fetch requestable user data.
 * @returns {Promise<UsersResponse>} A promise that resolves to the requestable user data response.
 */
const fetchRequestableUsers = async (
  userProps: RequestableUsersProps
): Promise<UsersResponse> => {
  const response = (await ExternalApiService.getRequestableUsers(
    userProps
  )) as UsersResponse;

  return response;
};

/**
 * Custom React hook to fetch and cache requestable user data from the external API using React Query.
 *
 * @function useGetRequestableUsers
 * @param {RequestableUsersProps} userProps - The user properties required for fetching requestable user data.
 * @returns {import('@tanstack/react-query').UseQueryResult<UsersResponse, unknown>} An object containing the query result, including the requestable user data, loading state, and potential errors.
 *
 */
const useGetRequestableUsers = (userProps: RequestableUsersProps) =>
  useQuery<UsersResponse>({
    queryKey: ['REQUESTABLE_USERS', userProps], // Unique query key
    queryFn: () => fetchRequestableUsers(userProps), // Function to fetch data
    refetchOnWindowFocus: false,
  });

export default useGetRequestableUsers;
